/**
 * Notification service API client — activity feed, unread count, mark-read.
 *
 * Requests are routed through the gateway (/api/v1/notifications/*) which
 * proxies to the notification_service. This keeps the frontend talking to
 * a single origin — no extra CORS config needed.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import type {
  MarkAllReadResponse,
  NotificationListResponse,
  NotificationResponse,
  PreferenceResponse,
  PreferenceUpdateRequest,
  UnreadCountResponse,
} from '@/types/api';
import axios from 'axios';

const notifApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

notifApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

/** List notifications (most recent first). */
export async function fetchNotifications(
  page = 1,
  pageSize = 10,
): Promise<NotificationListResponse> {
  const { data } = await notifApi.get<NotificationListResponse>(
    '/api/v1/notifications',
    { params: { page, page_size: pageSize } },
  );
  return data;
}

/** Get unread notification count for the bell badge. */
export async function fetchUnreadCount(): Promise<UnreadCountResponse> {
  const { data } = await notifApi.get<UnreadCountResponse>(
    '/api/v1/notifications/unread-count',
  );
  return data;
}

/** Mark a single notification as read. */
export async function markRead(
  notificationId: string,
): Promise<NotificationResponse> {
  const { data } = await notifApi.patch<NotificationResponse>(
    `/api/v1/notifications/${notificationId}/read`,
  );
  return data;
}

/** Mark all notifications as read. */
export async function markAllRead(): Promise<MarkAllReadResponse> {
  const { data } = await notifApi.post<MarkAllReadResponse>(
    '/api/v1/notifications/read-all',
  );
  return data;
}

// ---------------------------------------------------------------------------
// Notification preferences
// ---------------------------------------------------------------------------

/** Fetch current notification preferences. */
export async function fetchPreferences(): Promise<PreferenceResponse> {
  const { data } = await notifApi.get<PreferenceResponse>(
    '/api/v1/preferences',
  );
  return data;
}

/** Update notification preferences (partial update — only provided fields). */
export async function updatePreferences(
  prefs: PreferenceUpdateRequest,
): Promise<PreferenceResponse> {
  const { data } = await notifApi.put<PreferenceResponse>(
    '/api/v1/preferences',
    prefs,
  );
  return data;
}
